/**
 * Карточка недели одного человека (задача 116 §2.2, макап 3a).
 *
 * Два состояния: не подтверждённая (шапка + грид 7 дней + «Работы на проектах» +
 * футер-CTA «Подтвердить», заблокированная при пустом рабочем дне с объяснением) и
 * подтверждённая — success-свёртка (одна строка + «Раскрыть» → грид read-only +
 * «Отменить подтверждение» = дневной unapprove, §1 решений).
 *
 * Тона ячеек/бейджей — из tone-палитры (`--tone-*`/`badgeTones`), без сырых
 * цветов. Клик по ячейке рабочего дня (§2.3) открывает день в `TimesheetDayEditor`
 * через `onOpenDay` и до, и после подтверждения.
 */
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PersonNameLink } from "@/components/people/PersonNameLink";
import { ReworkBadge } from "@/components/timesheet/ReworkBadge";
import type { EntryDockSubject } from "@/components/approval/EntryDock";
import { formatHoursHalf, formatHoursHalfCompact } from "@/lib/timesheet";
import type { WeekDayCell, WeekPersonRow, WeekProjectWorkRow } from "@/api/approval";
import { WeekDayCellView } from "./WeekDayCellView";
import { attentionDayCount, firstEmptyDay } from "./helpers";

/** Кого/на какой день оформляем отсутствие (§2.3): минимум для `AbsenceFormDialog`. */
export interface AbsenceTarget {
  userId: number;
  name: string;
  defaultDate: string;
}

interface WeekPersonCardProps {
  row: WeekPersonRow;
  /** Мутация подтверждения/отмены для этого человека в полёте. */
  pending: boolean;
  onApprove: () => void;
  onUnapprove: () => void;
  /** Дата, выбранная в общем доке именно для этой строки; null = выбора нет. */
  selectedDate?: string | null;
  /** Клик по ячейке рабочего дня (§2.3) — открыть день в редакторе за этого человека. */
  onOpenDay?: (subject: EntryDockSubject, date: string) => void;
  /** «Оформить отсутствие» на пустом дне (§2.3) — открыть `AbsenceFormDialog`. */
  onOpenAbsence?: (target: AbsenceTarget) => void;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "");
}

export function WeekPersonCard({
  row,
  pending,
  onApprove,
  onUnapprove,
  selectedDate = null,
  onOpenDay,
  onOpenAbsence,
}: WeekPersonCardProps): React.ReactElement {
  const [expanded, setExpanded] = React.useState(false);

  if (row.confirmation_state === "not_required") {
    return (
      <NotRequiredCard row={row} expanded={expanded} onToggle={() => setExpanded((v) => !v)} />
    );
  }
  if (row.confirmation_state === "confirmed") {
    return (
      <ConfirmedCard
        row={row}
        expanded={expanded}
        pending={pending}
        onToggle={() => setExpanded((v) => !v)}
        onUnapprove={onUnapprove}
        selectedDate={selectedDate}
        onOpenDay={onOpenDay}
      />
    );
  }
  return (
    <PendingCard
      row={row}
      pending={pending}
      onApprove={onApprove}
      selectedDate={selectedDate}
      onOpenDay={onOpenDay}
      onOpenAbsence={onOpenAbsence}
    />
  );
}

function NotRequiredCard({
  row,
  expanded,
  onToggle,
}: {
  row: WeekPersonRow;
  expanded: boolean;
  onToggle: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div
      data-testid={`week-card-${row.user.id}`}
      data-confirmation-state="not_required"
      className="tone-neutral flex flex-col gap-3 rounded-[var(--radius-card)] p-3"
    >
      <div className="flex flex-wrap items-center gap-2.5">
        <span
          className="flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-chip)] bg-[var(--bg-panel-1)] font-mono text-xs font-semibold uppercase text-[var(--fg-2)]"
          aria-hidden="true"
        >
          {initials(row.user.display_name)}
        </span>
        <div className="min-w-0 flex-1">
          <PersonNameLink
            id={row.user.id}
            name={row.user.display_name}
            fullName={row.user.full_name}
            className="block truncate text-sm font-semibold text-[var(--fg-1)] hover:underline"
          />
        </div>
        <WeekTotals row={row} />
        <Badge tone="neutral" emphasis="soft">
          {t("week.card.notRequired")}
        </Badge>
        <Button
          variant="outline"
          size="sm"
          data-testid={`week-expand-${row.user.id}`}
          onClick={onToggle}
        >
          {expanded ? t("week.card.collapse") : t("week.card.expand")}
        </Button>
      </div>

      {expanded && <DayGrid row={row} selectedDate={null} />}
    </div>
  );
}

function PendingCard({
  row,
  pending,
  onApprove,
  selectedDate,
  onOpenDay,
  onOpenAbsence,
}: {
  row: WeekPersonRow;
  pending: boolean;
  onApprove: () => void;
  selectedDate: string | null;
  onOpenDay?: (subject: EntryDockSubject, date: string) => void;
  onOpenAbsence?: (target: AbsenceTarget) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const blocked = row.confirmation_state === "blocked";
  const problems = attentionDayCount(row);
  const reasons = row.blocking_reasons ?? [];
  const hasEmptyReason = reasons.some((reason) => reason.kind === "empty");
  const hasDraftReason = reasons.some((reason) => reason.kind === "draft");
  const blockedHint = hasDraftReason
    ? hasEmptyReason
      ? t("week.card.blockedHintMixed")
      : t("week.card.blockedHintDraft")
    : t("week.card.blockedHint");

  // Пустой день (нет сданного отчёта) → «внести за несдавшего» (caution-плашка
  // дока); день с отчётом → правка сданного (info-плашка). Выходной/отсутствие
  // нередактируемы (гейт в `WeekDayCellView`).
  const openCell = onOpenDay
    ? (dayCell: WeekDayCell): void =>
        onOpenDay(
          {
            userId: row.user.id,
            name: row.user.display_name,
            position: row.user.position,
            isEdit: dayCell.status !== null,
          },
          dayCell.date,
        )
    : undefined;

  // «Оформить отсутствие» преднабирается первым пустым рабочим днём — днём,
  // требующим внимания; диапазон руководитель расширяет в самом диалоге.
  const emptyDate = firstEmptyDay(row);
  const openAbsence =
    onOpenAbsence && emptyDate !== null
      ? (): void =>
          onOpenAbsence({
            userId: row.user.id,
            name: row.user.display_name,
            defaultDate: emptyDate,
          })
      : undefined;

  return (
    <div
      data-testid={`week-card-${row.user.id}`}
      data-confirmation-state={row.confirmation_state}
      data-confirmed="false"
      data-blocked={blocked ? "true" : "false"}
      className="flex flex-col gap-3 rounded-[var(--radius-card)] border bg-[var(--bg-panel-0)] p-3"
      style={{ borderColor: blocked ? "var(--tone-danger-border)" : "var(--rule-line)" }}
    >
      <CardHeader row={row} showProblems={blocked ? problems : 0} />
      <DayGrid row={row} selectedDate={selectedDate} onSelectCell={openCell} />
      <ProjectWorks works={row.project_works} />
      <div className="flex flex-col gap-1.5 border-t border-[var(--rule-faint)] pt-3">
        {blocked && (
          <span className="text-xs" style={{ color: "var(--tone-danger-fg)" }}>
            {blockedHint}
          </span>
        )}
        {/* Пустой день закрывается двумя путями (решение владельца §2): заполнить
            за человека (клик по ячейке → док) ИЛИ оформить отсутствие. Кнопка
            видна только при пустом дне — зеркало пары «Внести»/«Отсутствие»
            дневной консоли (`MissingPanel`). */}
        {openAbsence && (
          <Button
            variant="outline"
            size="default"
            className="w-full"
            disabled={pending}
            data-testid={`week-absence-${row.user.id}`}
            onClick={openAbsence}
          >
            {t("week.card.markAbsence")}
          </Button>
        )}
        <Button
          size="default"
          className="w-full"
          disabled={blocked || pending}
          data-testid={`week-approve-${row.user.id}`}
          onClick={onApprove}
        >
          {pending ? (
            t("timeApproval.approving")
          ) : (
            <>
              <Check className="h-4 w-4" />
              {t("timeApproval.approve")}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

function ConfirmedCard({
  row,
  expanded,
  pending,
  onToggle,
  onUnapprove,
  selectedDate,
  onOpenDay,
}: {
  row: WeekPersonRow;
  expanded: boolean;
  pending: boolean;
  onToggle: () => void;
  onUnapprove: () => void;
  selectedDate: string | null;
  onOpenDay?: (subject: EntryDockSubject, date: string) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const openCell = onOpenDay
    ? (dayCell: WeekDayCell): void =>
        onOpenDay(
          {
            userId: row.user.id,
            name: row.user.display_name,
            position: row.user.position,
            isEdit: true,
          },
          dayCell.date,
        )
    : undefined;
  return (
    <div
      data-testid={`week-card-${row.user.id}`}
      data-confirmation-state="confirmed"
      data-confirmed="true"
      className="tone-success flex flex-col gap-3 rounded-[var(--radius-card)] p-3"
    >
      <div className="flex flex-wrap items-center gap-2.5">
        <span
          className="flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-chip)] font-mono text-xs font-semibold uppercase"
          style={{
            background: "var(--tone-success-solid-bg)",
            color: "var(--tone-success-solid-fg)",
          }}
          aria-hidden="true"
        >
          {initials(row.user.display_name)}
        </span>
        <div className="min-w-0 flex-1">
          <PersonNameLink
            id={row.user.id}
            name={row.user.display_name}
            fullName={row.user.full_name}
            className="block truncate text-sm font-semibold text-[var(--fg-1)] hover:underline"
          />
        </div>
        <WeekTotals row={row} />
        <Badge tone="success" emphasis="soft">
          {t("week.card.confirmed")}
        </Badge>
        <Button
          variant="outline"
          size="sm"
          data-testid={`week-expand-${row.user.id}`}
          onClick={onToggle}
        >
          {expanded ? t("week.card.collapse") : t("week.card.expand")}
        </Button>
      </div>

      {expanded && (
        <div className="flex flex-col gap-3">
          <DayGrid row={row} selectedDate={selectedDate} onSelectCell={openCell} />
          <ProjectWorks works={row.project_works} />
          <div className="flex justify-end border-t border-[var(--rule-faint)] pt-2">
            <button
              type="button"
              disabled={pending}
              data-testid={`week-unapprove-${row.user.id}`}
              onClick={onUnapprove}
              className="font-mono text-[11px] text-[var(--accent-tech)] underline-offset-2 hover:underline disabled:opacity-50"
            >
              {t("week.card.unapprove")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CardHeader({
  row,
  showProblems,
}: {
  row: WeekPersonRow;
  showProblems: number;
}): React.ReactElement {
  const { t } = useTranslation();
  const subtitle = [
    row.user.position,
    row.user.tab_number ? t("week.card.tabNo", { number: row.user.tab_number }) : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <div className="flex flex-wrap items-center gap-2.5">
      <span
        className="flex size-9 shrink-0 items-center justify-center rounded-[var(--radius-chip)] font-mono text-xs font-semibold uppercase"
        style={{ background: "var(--bg-panel-1)", color: "var(--fg-2)" }}
        aria-hidden="true"
      >
        {initials(row.user.display_name)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <PersonNameLink
            id={row.user.id}
            name={row.user.display_name}
            fullName={row.user.full_name}
            className="truncate text-sm font-semibold text-[var(--fg-1)] hover:underline"
          />
          {showProblems > 0 && (
            <Badge tone="danger" emphasis="soft" data-testid={`week-problems-${row.user.id}`}>
              {t("week.card.problems", { count: showProblems })}
            </Badge>
          )}
        </div>
        {subtitle && (
          <div className="truncate font-mono text-[11px] text-[var(--fg-3)]">{subtitle}</div>
        )}
      </div>
      <WeekTotals row={row} />
    </div>
  );
}

function WeekTotals({ row }: { row: WeekPersonRow }): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div className="flex w-full flex-col gap-0.5 sm:w-auto sm:items-end">
      <span
        data-testid={`week-work-total-${row.user.id}`}
        className="font-mono text-xs tabular-nums text-[var(--fg-2)]"
      >
        {t("week.card.weekWork", {
          hours: formatHoursHalfCompact(row.week_work_hours_half),
        })}
      </span>
      <span
        data-testid={`week-declared-total-${row.user.id}`}
        className="font-mono text-xs tabular-nums text-[var(--fg-3)]"
      >
        {t("week.card.weekDeclared", {
          hours: formatHoursHalfCompact(row.week_declared_hours_half),
        })}
      </span>
    </div>
  );
}

function DayGrid({
  row,
  selectedDate,
  onSelectCell,
}: {
  row: WeekPersonRow;
  /** Controlled selection from `WeekFeedConsole.dock`; no local cell state. */
  selectedDate: string | null;
  /** Рабочие дни редактируемы и до, и после подтверждения; settled-состояния отсекает view. */
  onSelectCell?: (cell: WeekDayCell) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const now = new Date();
  return (
    <div className="flex flex-col gap-1.5">
      <span className="font-condensed text-xs uppercase tracking-[0.06em] text-[var(--fg-3)]">
        {t("week.card.daysLabel")}
      </span>
      <div
        className="grid grid-cols-1 items-stretch gap-1.5 sm:grid-cols-4 xl:grid-cols-7"
        data-testid={`week-days-${row.user.id}`}
      >
        {row.days.map((cell) => (
          <WeekDayCellView
            key={cell.date}
            cell={cell}
            testId={`week-day-${row.user.id}-${cell.date}`}
            selected={cell.date === selectedDate}
            onSelect={onSelectCell ? () => onSelectCell(cell) : undefined}
            now={now}
          />
        ))}
      </div>
    </div>
  );
}

function ProjectWorks({ works }: { works: WeekProjectWorkRow[] }): React.ReactElement | null {
  const { t } = useTranslation();
  if (works.length === 0) return null;

  // Группируем плоский список работ по проекту (проект → строки видов работ).
  const groups = new Map<number, { code: string; name: string; rows: WeekProjectWorkRow[] }>();
  for (const w of works) {
    const g = groups.get(w.project.id);
    if (g) g.rows.push(w);
    else groups.set(w.project.id, { code: w.project.code, name: w.project.name, rows: [w] });
  }

  return (
    <div className="flex flex-col gap-2 border-t border-[var(--rule-faint)] pt-3">
      <span className="font-condensed text-xs uppercase tracking-[0.06em] text-[var(--fg-3)]">
        {t("week.card.worksLabel")}
      </span>
      {[...groups.values()].map((group) => {
        const total = group.rows.reduce((sum, r) => sum + r.hours_half, 0);
        return (
          <div key={group.code} className="flex flex-col gap-1">
            <div className="flex items-baseline gap-2">
              <span className="font-mono text-xs font-semibold tabular-nums text-[var(--accent-tech)]">
                {group.code}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-[var(--fg-1)]">
                {group.name}
              </span>
              <span className="shrink-0 font-mono text-sm tabular-nums text-[var(--fg-2)]">
                {formatHoursHalf(total)}
              </span>
            </div>
            {group.rows.map((r) => (
              <WorkRow
                key={`${r.project.id}-${r.work_type.id}-${r.assembly_element?.id ?? "none"}`}
                row={r}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}

function WorkRow({ row }: { row: WeekProjectWorkRow }): React.ReactElement {
  const { t } = useTranslation();
  const notes = row.rework_notes?.filter((n) => n.trim()) ?? [];
  return (
    <div className="flex flex-col gap-1 pl-2">
      <div className="flex items-baseline gap-2">
        <span className="min-w-0 flex-1 truncate text-sm text-[var(--fg-2)]">
          {row.work_type.name}
        </span>
        {row.is_rework && <ReworkBadge isRework className="shrink-0" />}
        <span className="shrink-0 font-mono text-sm tabular-nums text-[var(--fg-1)]">
          {formatHoursHalf(row.hours_half)}
        </span>
      </div>
      {row.assembly_element && (
        <span
          className="truncate text-[length:var(--fs-11)] text-[var(--fg-3)]"
          data-testid={`week-work-element-${row.assembly_element.id}`}
        >
          {t("common.elementLabel", { name: row.assembly_element.name })}
        </span>
      )}
      {notes.length > 0 && (
        <div
          className="tone-info flex flex-col gap-0.5 rounded-[var(--radius-chip)] px-2 py-1.5 text-[11px]"
          style={{ borderLeft: "2px solid var(--accent-tech)" }}
        >
          {notes.map((note, i) => (
            <span key={i} className="min-w-0 truncate">
              {note}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
